/**
 * Climb log — the pure core of the daily climb log
 * (`gyms/{gymId}/climbLogs/{uid}_{YYYY-MM-DD}`), one copy for the client and
 * the Cloud Functions.
 *
 * The log is the EVENT stream: every climb (first send or repeat) appends one
 * entry. The `ascents` collection stays the projection that answers the app's
 * hottest question ("did I climb this route?") in O(1) via its deterministic
 * `{uid}_{routeId}` id.
 *
 * Doc shape:
 * ```
 * {
 *   userId, date: "YYYY-MM-DD", startedAt, updatedAt,
 *   routeIds: string[],   // array-contains index — the route-delete cascade needs it
 *   climbs: { [cid]: { routeId, at, kind, difficulty, discipline, holdColor, flash?, compId?, attempts? } },
 *           // kind "attempt" lives under `attempt_{routeId}` with { attempts, at, lastAt }
 *   totals: { climbs, sends, repeats, attempts, byDifficulty: { [key]: n } },
 *           // climbs / sends / repeats / byDifficulty: send + repeat only; attempts: sum of tries
 *   spray, sprayTotals, problemIds  // the Spraywall half — owned by the spray writer
 *   planId: null,         // reserved for the future training plan
 * }
 * ```
 *
 * `climbs` is a MAP (not an array) so a single entry can be removed with
 * `deleteField()` — `arrayRemove` would need the exact stored object, including
 * a server-written timestamp the client does not have.
 *
 * The `updateClimbLogStats` trigger runs `diffClimbMaps` + `aggregateRepeatDelta`
 * on every write; the client writes `computeClimbTotals` / `collectRouteIds` into
 * the doc. One implementation means the server-maintained counters cannot
 * disagree with what the client shows.
 */

/** Climb kinds. `attempt` is a route project day: the tries on a route that
 *  day without a send, one entry per route and day (`attemptEntryId`). It is
 *  activity, never a climb — see `isClimbEntry`. */
export const CLIMB_KIND = { SEND: "send", REPEAT: "repeat", ATTEMPT: "attempt" };

/** The kinds of a Spraywall day-log entry (`sprayDayLogs.entries.*.kind`).
 *  Every kind is activity (a project day too); only send + repeat are climbs.
 *  One list, so the server's presence events and the client's timeline rows
 *  accept exactly the same entries. */
export const SPRAY_LOG_KINDS = Object.freeze(["send", "repeat", "project"]);

/** Deterministic doc id — enforced by the Firestore rule so nobody can write
 *  an entry onto an arbitrary day. */
export function climbLogDocId(uid, date) {
  return `${uid}_${date}`;
}

/**
 * Normalize the many shapes a stored `at` can take (Firestore Timestamp from a
 * listener, plain `{seconds}` from an export, a Date, or raw millis).
 * Returns 0 when unusable so ordering degrades to "oldest" instead of throwing.
 */
export function climbAtMillis(entry) {
  const at = entry && entry.at;
  if (at == null) return 0;
  if (typeof at === "number") return at;
  if (typeof at.toMillis === "function") return at.toMillis();
  if (at instanceof Date) return at.getTime();
  if (typeof at.seconds === "number") return at.seconds * 1000;
  return 0;
}

/**
 * Diff two climbs maps into the entries that appeared and disappeared.
 *
 * This is what the `updateClimbLogStats` trigger runs on every write: the doc is
 * rewritten ~25 times on a busy evening, and only the delta may touch the
 * counters. Entries present in both are ignored. The one write path that edits
 * an existing entry is a repeated "Próbáltam" raising `attempts` of an
 * `attempt_*` entry — invisible here, and harmless, because attempts move no
 * repeat counter and `attemptDates` follows the attempt KEY set instead.
 */
export function diffClimbMaps(before, after) {
  const b = before || {};
  const a = after || {};
  const added = [];
  const removed = [];
  for (const [cid, entry] of Object.entries(a)) {
    if (entry && !b[cid]) added.push({ cid, ...entry });
  }
  for (const [cid, entry] of Object.entries(b)) {
    if (entry && !a[cid]) removed.push({ cid, ...entry });
  }
  return { added, removed };
}

/**
 * Recompute the denormalized totals from the climbs map.
 *
 * These totals are a display convenience only — every authoritative aggregate
 * (route counters, per-user gym stats, the gym-wide counter) is maintained by
 * the `updateClimbLogStats` Cloud Function from the same map, so a client that
 * writes wrong totals only misleads itself.
 *
 * `climbs` / `sends` / `repeats` / `byDifficulty` count `send` + `repeat` only
 * (`isClimbEntry`); `attempts` is the sum of the tries of the `attempt` entries.
 * Six writers and about a dozen readers rely on this split, so it is decided
 * here and nowhere else.
 */
export function computeClimbTotals(climbs) {
  const totals = { climbs: 0, sends: 0, repeats: 0, attempts: 0, byDifficulty: {} };
  for (const entry of Object.values(climbs || {})) {
    if (!entry) continue;
    if (entry.kind === CLIMB_KIND.ATTEMPT) {
      // A project day is tries, not a climb: it must stay out of every volume
      // figure that trusts `totals`. A missing or damaged count still means at
      // least one try happened, otherwise the entry would not exist.
      const n = Number(entry.attempts);
      totals.attempts += Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
      continue;
    }
    if (!isClimbEntry(entry)) continue;
    totals.climbs += 1;
    if (entry.kind === CLIMB_KIND.SEND) totals.sends += 1;
    else if (entry.kind === CLIMB_KIND.REPEAT) totals.repeats += 1;
    if (entry.difficulty) {
      totals.byDifficulty[entry.difficulty] = (totals.byDifficulty[entry.difficulty] || 0) + 1;
    }
  }
  return totals;
}

/**
 * Is this entry of the `climbs` map a climb (a first send or a repeat)?
 *
 * The map also holds `attempt` entries (route project days: tries without a
 * send). Every consumer that counts or scores the map's entries filters through
 * this one test, so a try can never pass as a climb, a send or a point. A reader
 * of the stored `totals` needs no filter: `computeClimbTotals` applies it.
 */
export function isClimbEntry(entry) {
  return !!entry && (entry.kind === CLIMB_KIND.SEND || entry.kind === CLIMB_KIND.REPEAT);
}

/** The deterministic key of a route's project-day entry: one per route and
 *  day, so a second "Próbáltam" raises `attempts` instead of adding an entry. */
export function attemptEntryId(routeId) {
  return `attempt_${routeId}`;
}

/** How many `attempt` entries the map holds — the number of routes tried that
 *  day. This (not the try count) is what `gymStats.attemptDates` stores. */
export function countAttemptEntries(climbs) {
  let n = 0;
  for (const entry of Object.values(climbs || {})) {
    if (entry && entry.kind === CLIMB_KIND.ATTEMPT) n += 1;
  }
  return n;
}

/** Distinct route ids in the map — mirrored into the doc's `routeIds` field so
 *  the route-delete cascade can find affected days with `array-contains`
 *  instead of scanning every log in the gym. */
export function collectRouteIds(climbs) {
  const set = new Set();
  for (const entry of Object.values(climbs || {})) {
    if (entry && entry.routeId) set.add(entry.routeId);
  }
  return [...set];
}

/**
 * Turn a before/after pair of climbs maps into the counter delta the
 * `updateClimbLogStats` trigger has to apply.
 *
 * Extracted from the trigger so it can be unit-tested without an emulator: this
 * is the arithmetic behind every repeat counter in the app (route.repeatCount,
 * ascents.repeatCount, the per-user volume fields and the gym-wide counter), and
 * a mistake here corrupts them silently — nothing downstream re-derives these
 * numbers from the raw log.
 *
 * ONLY `kind: "repeat"` entries move anything. A first send writes its `ascents`
 * doc and its log entry in one atomic batch, and `updateGymStats` already counts
 * that side; counting it here too would double every send.
 *
 * ADDED entries carrying a `compId` are skipped as well: `syncCompetitionClimbs`
 * writes a whole competition at once and applies those deltas folded (one
 * increment per route rather than one per competitor), because up to 200 people
 * can top the same boulder and Firestore caps a document at ~1 write/sec.
 * REMOVED entries are NOT filtered — the symmetry matters: a route hard-delete
 * or a gym-leave cascade strips those entries again, and if the removal were
 * skipped too, every counter the sync added would stay behind forever.
 *
 * `{ includeCompetition: true }` keeps the added `compId` repeats too. ONLY
 * `recomputeGymStats` passes it: it rebuilds a whole `gymStats` doc from the raw
 * logs, and that doc already holds the competition repeats (the sync merged
 * them in), so a recompute that skipped them would silently undercount every
 * competition gym. The live trigger keeps the default, because there the sync
 * has already applied those entries itself.
 *
 * @param {Object|null} beforeClimbs
 * @param {Object|null} afterClimbs
 * @param {{includeCompetition?: boolean}} [options]
 * @returns {{net:number, perRoute:Map<string,number>, perDifficulty:Object,
 *            lastAtByRoute:Map<string,number>, empty:boolean}}
 */
export function aggregateRepeatDelta(beforeClimbs, afterClimbs, { includeCompetition = false } = {}) {
  const { added, removed } = diffClimbMaps(beforeClimbs, afterClimbs);
  const addedRepeats = added.filter((e) => e.kind === CLIMB_KIND.REPEAT && (includeCompetition || !e.compId));
  const removedRepeats = removed.filter((e) => e.kind === CLIMB_KIND.REPEAT);

  const perRoute = new Map();
  const perDifficulty = {};
  const lastAtByRoute = new Map();
  let net = 0;

  // Aggregate before writing so N repeats on one route in a single batch
  // collapse into ONE increment instead of N racing writes to the same doc.
  for (const e of addedRepeats) {
    net += 1;
    if (e.routeId) {
      perRoute.set(e.routeId, (perRoute.get(e.routeId) || 0) + 1);
      const ms = climbAtMillis(e);
      if (ms && ms > (lastAtByRoute.get(e.routeId) || 0)) lastAtByRoute.set(e.routeId, ms);
    }
    if (e.difficulty) perDifficulty[e.difficulty] = (perDifficulty[e.difficulty] || 0) + 1;
  }
  for (const e of removedRepeats) {
    net -= 1;
    if (e.routeId) perRoute.set(e.routeId, (perRoute.get(e.routeId) || 0) - 1);
    if (e.difficulty) perDifficulty[e.difficulty] = (perDifficulty[e.difficulty] || 0) - 1;
  }

  return {
    net,
    perRoute,
    perDifficulty,
    lastAtByRoute,
    empty: addedRepeats.length === 0 && removedRepeats.length === 0,
  };
}

/**
 * The activity-day union: on which days was the climber active, and how much?
 *
 * Activity readers (heatmap, streak, active-day counts) must treat a day as
 * active when ANY source says so: a route climb, a Spraywall entry, or a route
 * that was only tried. Volume readers (points, ascents, volume charts) keep
 * reading `climbDates` alone, because spray never counts as route volume (§12,
 * D6). This helper is the one place that merges the sources, so a reader cannot
 * forget one of them.
 *
 * There is deliberately no add-on input (D53): the helper does not know whether
 * a gym's Spraywall add-on is currently on, so the past spray days of a gym that
 * switched it off still count as active days.
 *
 * It does not add the live "today" layer either. A reader that wants today's
 * not-yet-aggregated climbs passes `climbCountsByDate(climbDates, todayLog)` as
 * `climbDates`.
 *
 * The object signature is shared with the route project / attempts plan
 * (`attemptDates`, plan §4.5), so whichever plan ships first creates the helper
 * and the other only fills in a key.
 *
 * A contribution counts only when `Number(value)` is finite and > 0 (defence in
 * depth: a damaged counter must not invent an active day). A map that is
 * missing, null, an array or not an object is ignored. Inputs are never mutated.
 *
 * @param {{climbDates?: Object|null, sprayDates?: Object|null, attemptDates?: Object|null}|null} [sources]
 * @returns {Object<string, number>} `{ "YYYY-MM-DD": summed count }`; days without a positive contribution are absent
 */
export function activityDates(sources) {
  const { climbDates, sprayDates, attemptDates } = sources || {};
  const result = {};
  for (const map of [climbDates, sprayDates, attemptDates]) {
    if (!map || typeof map !== "object" || Array.isArray(map)) continue;
    for (const date of Object.keys(map)) {
      const n = Number(map[date]);
      if (Number.isFinite(n) && n > 0) result[date] = (result[date] || 0) + n;
    }
  }
  return result;
}
